import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Movie } from '../../types';
import { movieService } from '../../services/api';
import SubscriptionModal from '../../components/SubscriptionModal';
import { useAuth } from '../../context/AuthContext';
import './Movies.css';

const Movies: React.FC = () => {
  const { isAuthenticated, subscription, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<{ total: number; last_page: number; current_page: number } | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const genres = [
    'Action', 'Comedy', 'Drama', 'Horror', 'Romance', 
    'Sci-Fi', 'Mystery', 'Family', 'Animation', 'Thriller'
  ];

  const loadMovies = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      const filters: Record<string, any> = { page };
      
      if (searchQuery) filters.search = searchQuery;
      if (selectedGenre) filters.genre = selectedGenre;
      
      const response: any = await movieService.getMovies(page, filters);
      
      // Handle both array and paginated response
      if (Array.isArray(response)) {
        setMovies(response);
        setPagination(null);
      } else if (response && response.data) {
        setMovies(response.data);
        setPagination(response.pagination);
      } else {
        setMovies(response || []);
        setPagination(null);
      }
      
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load movies:', error);
      setMovies([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedGenre]);

  useEffect(() => {
    loadMovies(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedGenre]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedGenre) params.set('genre', selectedGenre);
    setSearchParams(params);
  }, [searchQuery, selectedGenre, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadMovies(1);
  };

  const changePage = (page: number) => {
    window.scrollTo(0, 0);
    loadMovies(page);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGenre('');
  };

  const getThumbnailUrl = (thumbnail: string) => {
    if (!thumbnail) return '/public/assets/images/no-image.jpg';
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    return `/public/uploads/thumbnails/${thumbnail}`;
  };

  // Handle movie click with subscription check
  const handleMovieClick = async (movie: Movie): Promise<boolean> => {
    if (!isAuthenticated) {
      window.location.href = `/login?redirect=/movies/${movie.id}`;
      return false;
    }

    // Admin users have full access
    if (user?.role === 'admin') {
      return true;
    }

    // Check subscription
    if (subscription?.has_subscription) {
      return true;
    }

    // Show subscription modal
    setShowSubscriptionModal(true);
    return false;
  };

  return (
    <div className="min-h-screen bg-netflix-darker">
      {/* Page Header */}
      <div className="bg-gradient-to-b from-black/80 to-transparent pt-8 pb-8">
        <div className="container mx-auto px-4 lg:px-8">
          <h1 className="text-3xl lg:text-5xl font-black mb-6">
            <i className="fas fa-film text-netflix-red mr-3"></i>
            Browse Movies
          </h1>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <form onSubmit={handleSearch} className="flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search movies..."
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-netflix-red text-white placeholder-gray-400"
              />
            </form>
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-netflix-red text-white sm:w-48"
            >
              <option value="">All Genres</option>
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>

          {/* Active Filters */}
          {(searchQuery || selectedGenre) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white/60 text-sm">Filters:</span>
              {searchQuery && (
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white flex items-center gap-2">
                  Search: {searchQuery}
                  <button onClick={() => setSearchQuery('')} className="hover:text-netflix-red">
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </span>
              )}
              {selectedGenre && (
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white flex items-center gap-2">
                  {selectedGenre}
                  <button onClick={() => setSelectedGenre('')} className="hover:text-netflix-red">
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="text-netflix-red text-sm hover:underline ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Movies Grid */}
      <div className="container mx-auto px-4 lg:px-8 pb-12">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[2/3] bg-white/10 rounded-lg"></div>
                <div className="h-3 bg-white/10 rounded mt-2"></div>
                <div className="h-2 bg-white/10 rounded mt-1 w-3/4"></div>
              </div>
            ))}
          </div>
        ) : movies.length === 0 ? (
          <div className="text-center py-16">
            <i className="fas fa-film text-6xl text-netflix-gray mb-4"></i>
            <p className="text-xl text-netflix-light-gray">No movies found 😢</p>
            <p className="text-netflix-gray mt-2">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="mt-4 px-6 py-3 bg-netflix-red hover:bg-red-700 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
              {movies.map((movie) => (
                <div
                  key={movie.id}
                  className="group cursor-pointer transform transition-all duration-300 hover:scale-105 hover:z-10"
                  onClick={() => handleMovieClick(movie).then(allowed => {
                    if (allowed) window.location.href = `/movies/${movie.id}`;
                  })}
                >
                  {/* Movie Poster */}
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-netflix-darker shadow-lg group-hover:shadow-2xl group-hover:shadow-netflix-red/30">
                    {/* CineClubs Logo */}
                    <img 
                      src="/public/assets/images/logoformovies.png" 
                      alt="CineClubs" 
                      className="movie-thumbnail-logo absolute top-2 left-2 w-10 h-10 z-10 opacity-90 group-hover:opacity-100 transition-opacity"
                    />
                    
                    <img
                      src={getThumbnailUrl(movie.thumbnail)}
                      alt={movie.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.dataset.tried) {
                          target.dataset.tried = 'true';
                          target.src = '/public/assets/images/no-image.jpg';
                        }
                      }}
                    />
                    
                    {/* Overlay on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                      <p className="text-sm font-semibold mb-1 line-clamp-2">{movie.title}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="bg-netflix-red/80 px-2 py-1 rounded truncate max-w-[60%]">{movie.genre}</span>
                        <span className="inline-flex items-center bg-yellow-400 text-black px-2 py-0.5 rounded font-bold">
                          <span className="font-black mr-1">IMDb</span>
                          <i className="fas fa-star text-black text-xs mr-0.5"></i>
                          {movie.rating || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Movie Info (Mobile) */}
                  <div className="mt-2 sm:hidden">
                    <h3 className="font-semibold text-sm line-clamp-1">{movie.title}</h3>
                    <div className="flex items-center justify-between text-xs text-netflix-light-gray mt-1">
                      <span className="truncate max-w-[50%]">{movie.genre}</span>
                      <span className="inline-flex items-center bg-yellow-400 text-black px-1.5 py-0.5 rounded text-[10px] font-bold">
                        <span className="font-black mr-0.5">IMDb</span>
                        <i className="fas fa-star text-black text-[8px] mr-0.5"></i>
                        {movie.rating || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div className="mt-12 flex justify-center">
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  {currentPage > 1 && (
                    <button
                      onClick={() => changePage(currentPage - 1)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      Previous
                    </button>
                  )}
                  
                  <span className="px-4 py-2 text-netflix-light-gray">
                    Page {currentPage} of {pagination.last_page}
                  </span>
                  
                  {currentPage < pagination.last_page && (
                    <button
                      onClick={() => changePage(currentPage + 1)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionModal onClose={() => setShowSubscriptionModal(false)} />
      )}
    </div>
  );
};

export default Movies;
